import React, { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import Swal from "sweetalert2";
import { FiKey, FiCheck, FiX, FiAlertTriangle, FiMinus, FiRefreshCw, FiSave, FiEye, FiEyeOff, FiCopy, FiPhone } from "react-icons/fi";
import { SiNotion, SiFirebase, SiTwilio } from "react-icons/si";

function ApiServices() {
  const [notionKey, setNotionKey] = useState("");
  const [fireflyKey, setFireflyKey] = useState("");
  const [notionSaving, setNotionSaving] = useState(false);
  const [fireflySaving, setFireflySaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showNotionKey, setShowNotionKey] = useState(false);
  const [showFireflyKey, setShowFireflyKey] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);

  // Twilio state
  const [twilioAccountSid, setTwilioAccountSid] = useState("");
  const [twilioAuthToken, setTwilioAuthToken] = useState("");
  const [twilioPhoneNumber, setTwilioPhoneNumber] = useState("");
  const [twilioSaving, setTwilioSaving] = useState(false);
  const [showTwilioToken, setShowTwilioToken] = useState(false);

  // Quo state
  const [quoApiKey, setQuoApiKey] = useState("");
  const [quoSaving, setQuoSaving] = useState(false);
  const [showQuoKey, setShowQuoKey] = useState(false);

  const [existingKeys, setExistingKeys] = useState([]);
  const [keyStatus, setKeyStatus] = useState({});

  const statusConfig = {
    valid: {
      icon: FiCheck,
      color: "#28a745",
      bgColor: "rgba(40, 167, 69, 0.1)",
      label: "Valid",
    },
    unauthorized: {
      icon: FiX,
      color: "#dc3545",
      bgColor: "rgba(220, 53, 69, 0.1)",
      label: "Invalid",
    },
    no_credits: {
      icon: FiAlertTriangle,
      color: "#ffc107",
      bgColor: "rgba(255, 193, 7, 0.1)",
      label: "No Credits",
    },
    rate_limited: {
      icon: FiAlertTriangle,
      color: "#ffc107",
      bgColor: "rgba(255, 193, 7, 0.1)",
      label: "Rate Limited",
    },
    error: {
      icon: FiAlertTriangle,
      color: "#ffc107",
      bgColor: "rgba(255, 193, 7, 0.1)",
      label: "Error",
    },
    not_verifiable: {
      icon: FiMinus,
      color: "#6c757d",
      bgColor: "rgba(108, 117, 125, 0.1)",
      label: "Not Verified",
    },
  };

  const renderStatusBadge = (keyName) => {
    const status = keyStatus[keyName.toLowerCase()];
    if (!status) return null;

    const config = statusConfig[status.status] || statusConfig.error;
    const IconComponent = config.icon;

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          padding: "6px 12px",
          borderRadius: "20px",
          background: config.bgColor,
          color: config.color,
          fontSize: "12px",
          fontWeight: 600,
        }}
      >
        <IconComponent size={14} />
        {config.label}
      </div>
    );
  };

  const fetchExistingKeys = async () => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.access_token) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_ZOOMEY_API_URL}is_exist`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );
      const data = await response.json();
      if (data.existing_keys && Array.isArray(data.existing_keys)) {
        const keyNames = data.existing_keys.map((k) => k.key_name);
        setExistingKeys(keyNames);

        const statusMap = {};
        data.existing_keys.forEach((keyObj) => {
          statusMap[keyObj.key_name.toLowerCase()] = {
            is_verified: keyObj.is_verified,
            status: keyObj.status,
            message: keyObj.message,
          };
        });
        setKeyStatus(statusMap);

        const existingLower = keyNames.map((k) => k.toLowerCase());
        if (existingLower.includes("notion")) setNotionKey("*".repeat(40));
        if (existingLower.includes("fireflies")) setFireflyKey("*".repeat(40));
        if (existingLower.includes("twilio")) {
          setTwilioAccountSid("*".repeat(34));
          setTwilioAuthToken("*".repeat(32));
          setTwilioPhoneNumber("*".repeat(12));
        }
        if (existingLower.includes("quo")) setQuoApiKey("*".repeat(40));
      }
    } catch (err) {
      console.error("Failed to fetch existing keys:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchExistingKeys();
  }, []);

  const handleSaveKey = async (keyName, apiKey, setSaving, setKey) => {
    setSaving(true);
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.access_token) {
      console.error("Session expired");
      setSaving(false);
      return;
    }
    try {
      const response = await fetch(
        `${process.env.REACT_APP_ZOOMEY_API_URL}store_api_key`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            key_name: keyName,
            api_key: apiKey,
          }),
        }
      );

      if (!response.ok) {
        let errorMsg = `Server error: ${response.status}`;
        try {
          const errJson = await response.json();
          if (errJson?.message) errorMsg = errJson.message;
          else if (errJson?.detail) errorMsg = errJson.detail;
        } catch {}
        throw new Error(errorMsg);
      }
      const existingLower = existingKeys.map((k) => k.toLowerCase());
      if (!existingLower.includes(keyName.toLowerCase())) {
        setExistingKeys((prev) => [...prev, keyName]);
      }
      setKey("*".repeat(40));
      Swal.fire({
        icon: "success",
        title: "Key Saved!",
        text: `Your ${keyName} API key saved successfully!`,
        background: "var(--background)",
        color: "var(--text)",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
      fetchExistingKeys();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Invalid Key",
        text: `${err.message}`,
        background: "var(--background)",
        color: "var(--text)",
        confirmButtonColor: "var(--teal-bg)",
        timer: 3000,
        showConfirmButton: true,
        confirmButtonText: "Retry",
      });
      fetchExistingKeys();
      console.error(`Failed to save ${keyName} key:`, err);
    }
    setSaving(false);
  };

  const handleNotionSave = (e) => {
    e.preventDefault();
    handleSaveKey("Notion", notionKey, setNotionSaving, setNotionKey);
  };

  const handleFireflySave = (e) => {
    e.preventDefault();
    handleSaveKey("Fireflies", fireflyKey, setFireflySaving, setFireflyKey);
  };

  const handleQuoSave = async (e) => {
    e.preventDefault();
    if (!quoApiKey || quoApiKey.startsWith("*")) return;
    setQuoSaving(true);
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) {
      console.error("Session expired");
      setQuoSaving(false);
      return;
    }
    try {
      const NEXT_API_URL = process.env.REACT_APP_NEXT_API_URL || "http://localhost:3000";
      const response = await fetch(`${NEXT_API_URL}/api/integrations/save-api-key`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: session.user.id,
          app_name: "Quo",
          api_key: quoApiKey,
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Server error: ${response.status}`);
      }
      const existingLower = existingKeys.map((k) => k.toLowerCase());
      if (!existingLower.includes("quo")) {
        setExistingKeys((prev) => [...prev, "Quo"]);
      }
      setQuoApiKey("*".repeat(40));
      Swal.fire({
        icon: "success",
        title: "Quo Connected!",
        text: "Your Quo API key saved successfully! Webhook auto-configured.",
        background: "var(--background)",
        color: "var(--text)",
        timer: 2000,
        showConfirmButton: false,
      });
      fetchExistingKeys();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Invalid Key",
        text: err.message,
        background: "var(--background)",
        color: "var(--text)",
        confirmButtonColor: "var(--teal-bg)",
        timer: 3000,
        showConfirmButton: true,
        confirmButtonText: "Retry",
      });
    }
    setQuoSaving(false);
  };

  const handleTwilioSave = async (e) => {
    e.preventDefault();
    if (!twilioAccountSid || !twilioAuthToken || !twilioPhoneNumber) {
      Swal.fire({
        icon: "warning",
        title: "Missing Fields",
        text: "Please fill in all Twilio fields",
        background: "var(--background)",
        color: "var(--text)",
      });
      return;
    }
    setTwilioSaving(true);
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      setTwilioSaving(false);
      return;
    }
    try {
      const response = await fetch(
        `${process.env.REACT_APP_ZOOMEY_API_URL}store_api_key`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            key_name: "Twilio",
            api_key: JSON.stringify({
              account_sid: twilioAccountSid,
              auth_token: twilioAuthToken,
              phone_number: twilioPhoneNumber,
            }),
          }),
        }
      );
      if (!response.ok) {
        let errorMsg = `Server error: ${response.status}`;
        try {
          const errJson = await response.json();
          if (errJson?.message) errorMsg = errJson.message;
        } catch {}
        throw new Error(errorMsg);
      }
      setTwilioAccountSid("*".repeat(34));
      setTwilioAuthToken("*".repeat(32));
      Swal.fire({
        icon: "success",
        title: "Twilio Connected!",
        text: "Your Twilio credentials saved successfully!",
        background: "var(--background)",
        color: "var(--text)",
        timer: 2000,
        showConfirmButton: false,
      });
      fetchExistingKeys();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Invalid Credentials",
        text: err.message,
        background: "var(--background)",
        color: "var(--text)",
      });
    }
    setTwilioSaving(false);
  };

  const copyWebhookUrl = () => {
    const webhookUrl = `${window.location.origin.replace(':3001', ':3000')}/api/twilio/webhook`;
    navigator.clipboard.writeText(webhookUrl);
    Swal.fire({
      icon: "success",
      title: "Copied!",
      text: "Webhook URL copied to clipboard",
      background: "var(--background)",
      color: "var(--text)",
      timer: 1500,
      showConfirmButton: false,
    });
  };

  const apiServices = [
    {
      id: "notion",
      name: "Notion",
      description: "Connect your Notion workspace for note syncing",
      icon: SiNotion,
      gradient: "linear-gradient(135deg, #000000 0%, #333333 100%)",
      value: notionKey,
      setValue: setNotionKey,
      saving: notionSaving,
      onSave: handleNotionSave,
      showKey: showNotionKey,
      setShowKey: setShowNotionKey,
    },
    {
      id: "fireflies",
      name: "Fireflies",
      description: "Integrate meeting transcriptions and summaries",
      icon: SiFirebase,
      gradient: "linear-gradient(135deg, #FF6B35 0%, #FF8C42 100%)",
      value: fireflyKey,
      setValue: setFireflyKey,
      saving: fireflySaving,
      onSave: handleFireflySave,
      showKey: showFireflyKey,
      setShowKey: setShowFireflyKey,
    },
  ];

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <FiRefreshCw
          style={{
            fontSize: "32px",
            color: "var(--teal-bg)",
            animation: "spin 1s linear infinite",
          }}
        />
        <span style={{ color: "var(--text)", opacity: 0.7 }}>Loading API services...</span>
        <style>
          {`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "24px",
        color: "var(--text)",
        backgroundColor: "var(--background)",
        minHeight: "100%",
      }}
    >
      {/* Header Section */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          marginBottom: "32px",
          paddingBottom: "24px",
          borderBottom: "1px solid var(--border-color)",
        }}
      >
        <div
          style={{
            width: "56px",
            height: "56px",
            borderRadius: "14px",
            background: "linear-gradient(135deg, var(--teal-bg), var(--orange-bg))",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 20px rgba(0, 150, 137, 0.3)",
          }}
        >
          <FiKey size={28} color="white" />
        </div>
        <div>
          <h2 style={{ fontWeight: 700, fontSize: "1.5rem", marginBottom: "4px" }}>
            API Services
          </h2>
          <p style={{ opacity: 0.7, fontSize: "14px", margin: 0 }}>
            Manage your third-party API integrations
          </p>
        </div>
      </div>

      {/* API Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(400px, 1fr))",
          gap: "24px",
        }}
      >
        {apiServices.map((service) => {
          const isHovered = hoveredCard === service.id;
          const hasExistingKey = existingKeys.map(k => k.toLowerCase()).includes(service.id);
          const IconComponent = service.icon;

          return (
            <form
              key={service.id}
              onSubmit={service.onSave}
              onMouseEnter={() => setHoveredCard(service.id)}
              onMouseLeave={() => setHoveredCard(null)}
              style={{
                background: "var(--input-box-bg)",
                borderRadius: "16px",
                border: `1px solid ${isHovered ? "var(--teal-bg)" : "var(--border-color)"}`,
                padding: "24px",
                transition: "all 0.3s ease",
                transform: isHovered ? "translateY(-4px)" : "translateY(0)",
                boxShadow: isHovered
                  ? "0 12px 40px rgba(0, 150, 137, 0.15)"
                  : "0 2px 8px rgba(0, 0, 0, 0.05)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Gradient Accent Bar */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "4px",
                  background: service.gradient,
                  opacity: isHovered ? 1 : 0.6,
                  transition: "opacity 0.3s ease",
                }}
              />

              {/* Service Header */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: "20px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                  <div
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "12px",
                      background: "var(--background)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid var(--border-color)",
                    }}
                  >
                    <IconComponent size={24} style={{ color: "var(--text)" }} />
                  </div>
                  <div>
                    <h3
                      style={{
                        fontSize: "18px",
                        fontWeight: 700,
                        marginBottom: "2px",
                        color: "var(--text)",
                      }}
                    >
                      {service.name}
                    </h3>
                    <p
                      style={{
                        fontSize: "13px",
                        opacity: 0.7,
                        margin: 0,
                      }}
                    >
                      {service.description}
                    </p>
                  </div>
                </div>
                {renderStatusBadge(service.name)}
              </div>

              {/* API Key Input */}
              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    color: "var(--text)",
                    fontSize: "13px",
                    fontWeight: 600,
                    marginBottom: "10px",
                    display: "block",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    opacity: 0.8,
                  }}
                >
                  API Key
                </label>
                <div style={{ position: "relative" }}>
                  <FiKey
                    style={{
                      position: "absolute",
                      left: "14px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "var(--teal-bg)",
                      fontSize: "18px",
                    }}
                  />
                  <input
                    required
                    type={service.showKey ? "text" : "password"}
                    value={service.value}
                    onChange={(e) => {
                      if (service.value.startsWith("*")) {
                        service.setValue(e.target.value.replace(/^\*+$/, ""));
                      } else {
                        service.setValue(e.target.value);
                      }
                    }}
                    onFocus={() => {
                      if (service.value.startsWith("*")) service.setValue("");
                    }}
                    placeholder={`Enter your ${service.name} API Key`}
                    style={{
                      width: "100%",
                      backgroundColor: "var(--background)",
                      border: "1px solid var(--border-color)",
                      borderRadius: "12px",
                      padding: "12px 44px",
                      color: "var(--text)",
                      fontSize: "14px",
                      transition: "all 0.2s ease",
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => service.setShowKey(!service.showKey)}
                    style={{
                      position: "absolute",
                      right: "14px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      color: "var(--text)",
                      opacity: 0.6,
                      cursor: "pointer",
                      padding: 0,
                      display: "flex",
                    }}
                  >
                    {service.showKey ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                  </button>
                </div>
              </div>

              {/* Save Button */}
              <button
                type="submit"
                disabled={service.saving || service.value.startsWith("*")}
                style={{
                  width: "100%",
                  padding: "12px 20px",
                  borderRadius: "10px",
                  border: "none",
                  background:
                    service.saving || service.value.startsWith("*")
                      ? "var(--border-color)"
                      : "linear-gradient(135deg, var(--teal-bg), #00b3a4)",
                  color:
                    service.saving || service.value.startsWith("*")
                      ? "var(--text)"
                      : "var(--textwhite)",
                  fontWeight: 600,
                  fontSize: "14px",
                  cursor:
                    service.saving || service.value.startsWith("*")
                      ? "not-allowed"
                      : "pointer",
                  transition: "all 0.2s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  opacity: service.saving || service.value.startsWith("*") ? 0.6 : 1,
                }}
              >
                {service.saving ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <FiSave size={16} />
                    {hasExistingKey ? "Update Key" : "Save Key"}
                  </>
                )}
              </button>
            </form>
          );
        })}

        {/* Twilio Card - Special multi-field form */}
        <form
          onSubmit={handleTwilioSave}
          onMouseEnter={() => setHoveredCard("twilio")}
          onMouseLeave={() => setHoveredCard(null)}
          style={{
            background: "var(--input-box-bg)",
            borderRadius: "16px",
            border: `1px solid ${hoveredCard === "twilio" ? "var(--teal-bg)" : "var(--border-color)"}`,
            padding: "24px",
            transition: "all 0.3s ease",
            transform: hoveredCard === "twilio" ? "translateY(-4px)" : "translateY(0)",
            boxShadow: hoveredCard === "twilio"
              ? "0 12px 40px rgba(0, 150, 137, 0.15)"
              : "0 2px 8px rgba(0, 0, 0, 0.05)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Gradient Accent Bar */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: "linear-gradient(135deg, #F22F46 0%, #FF6B6B 100%)",
              opacity: hoveredCard === "twilio" ? 1 : 0.6,
              transition: "opacity 0.3s ease",
            }}
          />

          {/* Service Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: "20px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "12px",
                  background: "var(--background)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid var(--border-color)",
                }}
              >
                <SiTwilio size={24} style={{ color: "#F22F46" }} />
              </div>
              <div>
                <h3
                  style={{
                    fontSize: "18px",
                    fontWeight: 700,
                    marginBottom: "2px",
                    color: "var(--text)",
                  }}
                >
                  Twilio SMS
                </h3>
                <p style={{ fontSize: "13px", opacity: 0.7, margin: 0 }}>
                  Send and receive SMS messages
                </p>
              </div>
            </div>
            {renderStatusBadge("Twilio")}
          </div>

          {/* Account SID */}
          <div style={{ marginBottom: "12px" }}>
            <label
              style={{
                color: "var(--text)",
                fontSize: "13px",
                fontWeight: 600,
                marginBottom: "8px",
                display: "block",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                opacity: 0.8,
              }}
            >
              Account SID
            </label>
            <input
              type="text"
              value={twilioAccountSid}
              onChange={(e) => {
                if (twilioAccountSid.startsWith("*")) {
                  setTwilioAccountSid(e.target.value.replace(/^\*+$/, ""));
                } else {
                  setTwilioAccountSid(e.target.value);
                }
              }}
              onFocus={() => {
                if (twilioAccountSid.startsWith("*")) {
                  setTwilioAccountSid("");
                  setTwilioAuthToken("");
                  setTwilioPhoneNumber("");
                }
              }}
              placeholder="AC..."
              style={{
                width: "100%",
                backgroundColor: "var(--background)",
                border: "1px solid var(--border-color)",
                borderRadius: "10px",
                padding: "10px 14px",
                color: "var(--text)",
                fontSize: "14px",
              }}
            />
          </div>

          {/* Auth Token */}
          <div style={{ marginBottom: "12px" }}>
            <label
              style={{
                color: "var(--text)",
                fontSize: "13px",
                fontWeight: 600,
                marginBottom: "8px",
                display: "block",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                opacity: 0.8,
              }}
            >
              Auth Token
            </label>
            <div style={{ position: "relative" }}>
              <input
                type={showTwilioToken ? "text" : "password"}
                value={twilioAuthToken}
                onChange={(e) => {
                  if (twilioAuthToken.startsWith("*")) {
                    setTwilioAuthToken(e.target.value.replace(/^\*+$/, ""));
                  } else {
                    setTwilioAuthToken(e.target.value);
                  }
                }}
                onFocus={() => {
                  if (twilioAuthToken.startsWith("*")) {
                    setTwilioAccountSid("");
                    setTwilioAuthToken("");
                    setTwilioPhoneNumber("");
                  }
                }}
                placeholder="Your auth token"
                style={{
                  width: "100%",
                  backgroundColor: "var(--background)",
                  border: "1px solid var(--border-color)",
                  borderRadius: "10px",
                  padding: "10px 44px 10px 14px",
                  color: "var(--text)",
                  fontSize: "14px",
                }}
              />
              <button
                type="button"
                onClick={() => setShowTwilioToken(!showTwilioToken)}
                style={{
                  position: "absolute",
                  right: "14px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  color: "var(--text)",
                  opacity: 0.6,
                  cursor: "pointer",
                  padding: 0,
                  display: "flex",
                }}
              >
                {showTwilioToken ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              </button>
            </div>
          </div>

          {/* Phone Number */}
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                color: "var(--text)",
                fontSize: "13px",
                fontWeight: 600,
                marginBottom: "8px",
                display: "block",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                opacity: 0.8,
              }}
            >
              Twilio Phone Number
            </label>
            <input
              type="text"
              value={twilioPhoneNumber}
              onChange={(e) => {
                if (twilioPhoneNumber.startsWith("*")) {
                  setTwilioPhoneNumber(e.target.value.replace(/^\*+$/, ""));
                } else {
                  setTwilioPhoneNumber(e.target.value);
                }
              }}
              onFocus={() => {
                if (twilioPhoneNumber.startsWith("*")) {
                  setTwilioAccountSid("");
                  setTwilioAuthToken("");
                  setTwilioPhoneNumber("");
                }
              }}
              placeholder="+1234567890"
              style={{
                width: "100%",
                backgroundColor: "var(--background)",
                border: "1px solid var(--border-color)",
                borderRadius: "10px",
                padding: "10px 14px",
                color: "var(--text)",
                fontSize: "14px",
              }}
            />
          </div>

          {/* Webhook URL */}
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                color: "var(--text)",
                fontSize: "13px",
                fontWeight: 600,
                marginBottom: "8px",
                display: "block",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                opacity: 0.8,
              }}
            >
              Webhook URL (for incoming SMS)
            </label>
            <div style={{ display: "flex", gap: "8px" }}>
              <input
                type="text"
                readOnly
                value={`${window.location.origin.replace(':3001', ':3000')}/api/twilio/webhook`}
                style={{
                  flex: 1,
                  backgroundColor: "var(--background)",
                  border: "1px solid var(--border-color)",
                  borderRadius: "10px",
                  padding: "10px 14px",
                  color: "var(--text)",
                  fontSize: "13px",
                  opacity: 0.8,
                }}
              />
              <button
                type="button"
                onClick={copyWebhookUrl}
                style={{
                  padding: "10px 14px",
                  borderRadius: "10px",
                  border: "1px solid var(--border-color)",
                  background: "var(--background)",
                  color: "var(--text)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <FiCopy size={16} />
              </button>
            </div>
            <p style={{ fontSize: "11px", opacity: 0.6, marginTop: "6px", marginBottom: 0 }}>
              Add this URL in Twilio Console → Phone Numbers → Messaging Configuration
            </p>
          </div>

          {/* Save Button */}
          <button
            type="submit"
            disabled={twilioSaving || (twilioAccountSid.startsWith("*") && twilioAuthToken.startsWith("*"))}
            style={{
              width: "100%",
              padding: "12px 20px",
              borderRadius: "10px",
              border: "none",
              background:
                twilioSaving || (twilioAccountSid.startsWith("*") && twilioAuthToken.startsWith("*"))
                  ? "var(--border-color)"
                  : "linear-gradient(135deg, #F22F46, #FF6B6B)",
              color:
                twilioSaving || (twilioAccountSid.startsWith("*") && twilioAuthToken.startsWith("*"))
                  ? "var(--text)"
                  : "white",
              fontWeight: 600,
              fontSize: "14px",
              cursor:
                twilioSaving || (twilioAccountSid.startsWith("*") && twilioAuthToken.startsWith("*"))
                  ? "not-allowed"
                  : "pointer",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              opacity: twilioSaving || (twilioAccountSid.startsWith("*") && twilioAuthToken.startsWith("*")) ? 0.6 : 1,
            }}
          >
            {twilioSaving ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : (
              <>
                <FiSave size={16} />
                {existingKeys.map(k => k.toLowerCase()).includes("twilio") ? "Update Credentials" : "Save Credentials"}
              </>
            )}
          </button>
        </form>

        {/* Quo Card - Single API key field */}
        <form
          onSubmit={handleQuoSave}
          onMouseEnter={() => setHoveredCard("quo")}
          onMouseLeave={() => setHoveredCard(null)}
          style={{
            background: "var(--input-box-bg)",
            borderRadius: "16px",
            border: `1px solid ${hoveredCard === "quo" ? "var(--teal-bg)" : "var(--border-color)"}`,
            padding: "24px",
            transition: "all 0.3s ease",
            transform: hoveredCard === "quo" ? "translateY(-4px)" : "translateY(0)",
            boxShadow: hoveredCard === "quo"
              ? "0 12px 40px rgba(91, 95, 199, 0.15)"
              : "0 2px 8px rgba(0, 0, 0, 0.05)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: "linear-gradient(135deg, #5B5FC7 0%, #7B7FE7 100%)",
              opacity: hoveredCard === "quo" ? 1 : 0.6,
              transition: "opacity 0.3s ease",
            }}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: "20px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "12px",
                  background: "var(--background)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid var(--border-color)",
                }}
              >
                <FiPhone size={24} style={{ color: "#5B5FC7" }} />
              </div>
              <div>
                <h3
                  style={{
                    fontSize: "18px",
                    fontWeight: 700,
                    marginBottom: "2px",
                    color: "var(--text)",
                  }}
                >
                  Quo SMS
                </h3>
                <p style={{ fontSize: "13px", opacity: 0.7, margin: 0 }}>
                  Send and receive SMS via Quo (OpenPhone)
                </p>
              </div>
            </div>
            {renderStatusBadge("Quo")}
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                color: "var(--text)",
                fontSize: "13px",
                fontWeight: 600,
                marginBottom: "10px",
                display: "block",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                opacity: 0.8,
              }}
            >
              API Key
            </label>
            <div style={{ position: "relative" }}>
              <FiKey
                style={{
                  position: "absolute",
                  left: "14px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#5B5FC7",
                  fontSize: "18px",
                }}
              />
              <input
                required
                type={showQuoKey ? "text" : "password"}
                value={quoApiKey}
                onChange={(e) => {
                  if (quoApiKey.startsWith("*")) {
                    setQuoApiKey(e.target.value.replace(/^\*+$/, ""));
                  } else {
                    setQuoApiKey(e.target.value);
                  }
                }}
                onFocus={() => {
                  if (quoApiKey.startsWith("*")) setQuoApiKey("");
                }}
                placeholder="Enter your Quo API Key"
                style={{
                  width: "100%",
                  backgroundColor: "var(--background)",
                  border: "1px solid var(--border-color)",
                  borderRadius: "12px",
                  padding: "12px 44px",
                  color: "var(--text)",
                  fontSize: "14px",
                  transition: "all 0.2s ease",
                }}
              />
              <button
                type="button"
                onClick={() => setShowQuoKey(!showQuoKey)}
                style={{
                  position: "absolute",
                  right: "14px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  color: "var(--text)",
                  opacity: 0.6,
                  cursor: "pointer",
                  padding: 0,
                  display: "flex",
                }}
              >
                {showQuoKey ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              </button>
            </div>
            <p style={{ fontSize: "11px", opacity: 0.6, marginTop: "6px", marginBottom: 0 }}>
              Get your API key from Quo Settings → API tab. Webhook for incoming SMS is auto-configured.
            </p>
          </div>

          <button
            type="submit"
            disabled={quoSaving || quoApiKey.startsWith("*")}
            style={{
              width: "100%",
              padding: "12px 20px",
              borderRadius: "10px",
              border: "none",
              background:
                quoSaving || quoApiKey.startsWith("*")
                  ? "var(--border-color)"
                  : "linear-gradient(135deg, #5B5FC7, #7B7FE7)",
              color:
                quoSaving || quoApiKey.startsWith("*")
                  ? "var(--text)"
                  : "white",
              fontWeight: 600,
              fontSize: "14px",
              cursor:
                quoSaving || quoApiKey.startsWith("*")
                  ? "not-allowed"
                  : "pointer",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              opacity: quoSaving || quoApiKey.startsWith("*") ? 0.6 : 1,
            }}
          >
            {quoSaving ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : (
              <>
                <FiSave size={16} />
                {existingKeys.map(k => k.toLowerCase()).includes("quo") ? "Update Key" : "Save Key"}
              </>
            )}
          </button>
        </form>
      </div>

      {/* Info Section */}
      <div
        style={{
          marginTop: "32px",
          padding: "20px",
          borderRadius: "12px",
          background: "rgba(0, 150, 137, 0.08)",
          border: "1px solid rgba(0, 150, 137, 0.2)",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
          <FiAlertTriangle size={20} style={{ color: "var(--teal-bg)", marginTop: "2px" }} />
          <div>
            <h4 style={{ fontSize: "14px", fontWeight: 600, marginBottom: "6px" }}>
              Security Note
            </h4>
            <p style={{ fontSize: "13px", opacity: 0.8, margin: 0, lineHeight: 1.6 }}>
              Your API keys are encrypted and stored securely. We never share your keys with third parties.
              Keys are validated upon saving to ensure they work correctly.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ApiServices;
